import { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { uploadImage } from '../services/api';

/**
 * ImageUpload Component
 * A reusable component for uploading images with preview and drag-and-drop support
 * 
 * Props:
 * - value: current image URL
 * - onChange: callback when image is uploaded (receives URL)
 * - onError: callback for errors
 * - className: additional CSS classes
 * - aspectRatio: 'square' | 'video' | 'banner' | 'auto'
 * - maxSizeMB: maximum file size in MB (default: 10)
 * - placeholder: placeholder text
 * - showPreview: whether to show preview (default: true)
 * - disabled: whether upload is disabled
 */
const ImageUpload = ({
  value,
  onChange,
  onError,
  className = '',
  aspectRatio = 'auto',
  maxSizeMB = 10,
  placeholder = 'Click or drag to upload image',
  showPreview = true,
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(value || null);
  const fileInputRef = useRef(null);

  const aspectRatioClasses = {
    square: 'aspect-square',
    video: 'aspect-video',
    banner: 'aspect-[3/1]',
    auto: 'min-h-[200px]',
  };

  const validateFile = useCallback((file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Please upload JPG, PNG, GIF, or WebP.';
    }
    
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `File too large. Maximum size is ${maxSizeMB}MB.`;
    }
    
    return null;
  }, [maxSizeMB]);

  const handleUpload = useCallback(async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      if (onError) onError(validationError);
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    // Create local preview immediately
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    try {
      // Simulate progress (since we don't have real progress tracking)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      const response = await uploadImage(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success && response.url) {
        // Revoke local preview and use server URL
        URL.revokeObjectURL(localPreview);
        setPreviewUrl(response.url);
        if (onChange) onChange(response.url);
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (err) {
      URL.revokeObjectURL(localPreview);
      setPreviewUrl(value || null);
      const errorMsg = err.message || 'Failed to upload image';
      setError(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  }, [validateFile, onChange, onError, value]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [disabled, handleUpload]);

  const handleRemove = () => {
    setPreviewUrl(null);
    setError(null);
    if (onChange) onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isUploading}
      />

      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative overflow-hidden rounded-lg border-2 border-dashed transition-all cursor-pointer
          ${aspectRatioClasses[aspectRatio]}
          ${isDragging ? 'border-yellow-500 bg-yellow-50' : 'border-gray-300 hover:border-gray-400'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${previewUrl && showPreview ? 'border-solid border-gray-200' : ''}
        `}
      >
        {/* Preview Image */}
        {previewUrl && showPreview ? (
          <div className="relative w-full h-full">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-full object-cover"
            />
            
            {/* Overlay with remove button */}
            <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              {!isUploading && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClick();
                    }}
                    className="bg-white/90 hover:bg-white"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Change
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove();
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          /* Upload Placeholder */
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
            {isUploading ? (
              <>
                <Loader2 className="w-10 h-10 text-yellow-500 animate-spin mb-3" />
                <p className="text-sm text-gray-600">Uploading...</p>
                <div className="w-32 h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className={`p-3 rounded-full mb-3 ${isDragging ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                  <ImageIcon className={`w-8 h-8 ${isDragging ? 'text-yellow-600' : 'text-gray-400'}`} />
                </div>
                <p className="text-sm font-medium text-gray-700">{placeholder}</p>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, PNG, GIF, WebP up to {maxSizeMB}MB
                </p>
              </>
            )}
          </div>
        )}

        {/* Upload progress overlay */}
        {isUploading && previewUrl && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="text-center text-white">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              <p className="text-sm">Uploading... {uploadProgress}%</p>
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 mt-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Success indicator */}
      {previewUrl && !isUploading && !error && (
        <div className="flex items-center gap-2 mt-2 text-sm text-green-600">
          <Check className="w-4 h-4 flex-shrink-0" />
          <span>Image uploaded</span>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
